/** Resolve public fragments while retaining unique DOM IDs for copied import groups. */
export function installImportedAnchorNavigation(root: HTMLElement): () => void {
	const doc = root.ownerDocument;
	const view = doc.defaultView!;
	const findTarget = (hash: string, origin?: Element): Element | null => {
		let name: string;
		try {
			name = decodeURIComponent(hash.slice(1));
		} catch {
			return null;
		}
		if (!name) return null;
		const selector = `[data-biz-import-anchor="${CSS.escape(name)}"]`;
		const group = origin?.closest('.biz-import-group, .biz-html-code');
		// A link in a copied group targets that group. Shared URLs target the first occurrence.
		const localGroup = group?.closest('.biz-import-group') ?? group;
		return localGroup?.querySelector(selector) ?? doc.getElementById(name) ?? doc.querySelector(selector);
	};
	const scrollFromHash = () => {
		const target = findTarget(view.location.hash);
		if (target && root.contains(target)) target.scrollIntoView({ block: 'start' });
	};
	const onClick = (event: MouseEvent) => {
		if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
			return;
		const anchor = (event.target as Element).closest?.('a[href]');
		const hash = anchor?.getAttribute('href');
		if (!anchor || !hash?.startsWith('#') || anchor.hasAttribute('download')) return;
		if (anchor.getAttribute('target') && anchor.getAttribute('target') !== '_self') return;
		const target = findTarget(hash, anchor);
		if (!target || !root.contains(target)) return;
		event.preventDefault();
		if (view.location.hash !== hash) view.history.pushState(null, '', hash);
		target.scrollIntoView({ block: 'start' });
	};
	doc.addEventListener('click', onClick);
	view.addEventListener('hashchange', scrollFromHash);
	const frame = view.requestAnimationFrame(scrollFromHash);
	return () => {
		doc.removeEventListener('click', onClick);
		view.removeEventListener('hashchange', scrollFromHash);
		view.cancelAnimationFrame(frame);
	};
}
